import React, { useState, useMemo } from 'react';
import {
  Row, Col, Form, Checkbox, Upload, Dropdown, message, Button
} from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { Pen2 } from '@solar-icons/react';
import StyledSelect from '../CustomAntdComponents/StyledSelect';
import StyledInput from '../CustomAntdComponents/StyledInput';
import { genderOptions, titleOptions, formatOcrResponse } from './helper';
import { useI18nContext } from '../../utils/I18n';
import translations from '../../screens/PassengerDetails/translations';
import {
  validateEmailRequired,
  validateNameRequired,
  validatePhoneRequired,
  validateRequired
} from "../../utils/helpers";
import PhoneNumberInput from "../PhoneNumberInput";
import {
  CheckBoxLabel,
  InfoWrapper,
  PrefillSectionWrapper,
  PrefillHeader,
  PrefillActions,
  StyledSavedProfileButton,
  StyledUploadButton,
  ProfileMenuItemWrapper,
  ProfileAvatar,
  ProfileInfo,
  ProfileName,
  ProfileDetails,
  SelectedProfileCard,
} from "./styles";
import { useRoadSearchStore } from "../../store/roadSearch/roadSearchStore";
import { StyledParagraph, StyledTitle } from "../CustomAntdComponents/Typography";
import { ReactComponent as BulbIcon } from '../../assets/images/bulb-filled-icon.svg';
import { useAuthStore } from '../../store/auth/authStore';
import { uploadPassportOcrApi } from '../../utils/api';

type Props = {
  field: any,
  passengerType: string,
  fieldName?: string,
}
function RoadPassengerForm({
  field, passengerType, fieldName
}: Props) {
  const {
    state: { t },
  } = useI18nContext();

  const {
    formData: {
      numberOfAdults,
    }
  } = useRoadSearchStore()


  const isAdult = passengerType === 'ADT';
  const form = Form.useFormInstance();

  const [isUploadingOcr, setIsUploadingOcr] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleOCR = async (file: any) => {
    setErrorMessage("");
    setIsUploadingOcr(true);
    message.loading({ content: 'Extracting data...', key: 'ocr' });
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await uploadPassportOcrApi(formData);

      if (response.status !== 200) {
        const errorResponse = response?.data?.detail || response?.data?.Message || 'Failed to extract passport data';
        message.error({ content: errorResponse, key: 'ocr' });
        setErrorMessage(errorResponse);
        return false;
      }
      setErrorMessage("");
      const ocr = response.data?.Data;
      const errors = response.data?.Errors;

      if (errors && errors.length > 0) {
        message.warning({ content: errors.join(', '), key: 'ocr' });
      }

      const ocrData = formatOcrResponse(ocr);
      const roadOcrData = {
        firstName: ocrData.firstName,
        lastName: ocrData.lastName,
        namePrefix: ocrData.title,
        gender: ocrData.gender,
        documentId: ocrData.passportNumber || ocrData?.idNumber || "",
      };

      const currentValues = form.getFieldsValue();
      const arrayName = fieldName || 'adults';
      const passengers = [...(currentValues[arrayName] || [])];

      if (passengers[field.name]) {
        passengers[field.name] = { ...passengers[field.name], ...roadOcrData };
      } else {
        passengers[field.name] = roadOcrData;
      }

      form.setFieldsValue({ [arrayName]: passengers });
      message.success({ content: 'Passport data extracted!', key: 'ocr' });
    } catch (error: any) {
      const errMsg = error?.data?.Message || error?.message || 'Failed to process passport data';
      setErrorMessage(errMsg);
      message.error({ content: errMsg, key: 'ocr' });
    } finally {
      setIsUploadingOcr(false);
    }

    return false;
  };

  const { isLoggedIn, travelers } = useAuthStore();
  const [selectedProfile, setSelectedProfile] = useState<any>(null);

  const profiles = useMemo(() => {
    if (!travelers || travelers.length === 0) return [];
    return travelers.map((item: any) => ({
      label: `${item.FirstName} ${item.LastName}`,
      value: item.Id,
      data: {
        firstName: item.FirstName,
        lastName: item.LastName,
        namePrefix: item.Title,
        gender: item.Gender === 0 ? 'male' : 'female',
        email: item.Email || '',
        phoneNumber: item.PhoneNumber || '',
        address: item.Address || '',
        documentId: item.PassportNumber || '',
        primary: item.IsPrimary
      }
    }));
  }, [travelers]);

  const handleProfileSelect = (value: string) => {
    const profile = profiles.find(p => p.value === value);
    if (profile) {
      setSelectedProfile(profile);
      const currentValues = form.getFieldsValue();
      const arrayName = fieldName || 'adults';
      const passengers = [...(currentValues[arrayName] || [])];

      if (passengers[field.name]) {
        passengers[field.name] = {
          ...passengers[field.name],
          ...profile.data
        };
      } else {
        passengers[field.name] = profile.data;
      }
      form.setFieldsValue({ [arrayName]: passengers });
    }
  };

  const profileMenu = {
    items: profiles.length > 0
      ? profiles.map(p => ({
        key: p.value,
        label: (
          <ProfileMenuItemWrapper>
            <ProfileAvatar>
              {p.data.firstName ? p.data.firstName[0] : ''}{p.data.lastName ? p.data.lastName[0] : ''}
            </ProfileAvatar>
            <ProfileInfo>
              <ProfileName>
                {p.data.firstName} {p.data.lastName} {p.data.primary && <span>(Primary traveller)</span>}
              </ProfileName>
              <ProfileDetails>
                {p.data.email ? `${p.data.email} • ` : ''}
              </ProfileDetails>
            </ProfileInfo>
          </ProfileMenuItemWrapper>
        )
      }))
      : [
        {
          key: 'no-data',
          disabled: true,
          label: (
            <div style={{ padding: '8px 12px', color: '#9CA3AF', textAlign: 'center', fontSize: '13px' }}>
              No saved profiles found
            </div>
          )
        }
      ],
    onClick: ({ key }: any) => {
      if (key !== 'no-data') {
        handleProfileSelect(key);
      }
    }
  };

  return (
    <Row gutter={16}>

      <Col span={24}>
        <div style={{ marginBottom: "20px" }}>
          {selectedProfile ? (
            <SelectedProfileCard>
              <ProfileMenuItemWrapper style={{ padding: 0 }}>
                <ProfileAvatar>
                  {selectedProfile.data.firstName ? selectedProfile.data.firstName[0] : ''}{selectedProfile.data.lastName ? selectedProfile.data.lastName[0] : ''}
                </ProfileAvatar>
                <ProfileInfo>
                  <ProfileName>
                    {selectedProfile.data.firstName} {selectedProfile.data.lastName} {selectedProfile.data.primary && <span>(Primary traveller)</span>}
                  </ProfileName>
                  <ProfileDetails>
                    {selectedProfile.data.email ? `${selectedProfile.data.email} • ` : ''}
                  </ProfileDetails>
                </ProfileInfo>
              </ProfileMenuItemWrapper>
              <Button type="text" icon={<Pen2 size={24} color="#6B7280" />} onClick={() => setSelectedProfile(null)} />
            </SelectedProfileCard>
          ) : (
            <PrefillSectionWrapper className={`${errorMessage ? 'has-error' : ''}`}>
              <PrefillHeader>
                <BulbIcon className={`${errorMessage ? 'has-error' : ''}`} />
                <StyledTitle level={4} className={`${errorMessage ? 'has-error' : ''}`} >{errorMessage ? "Image upload error" : "Pre-fill your details"}</StyledTitle>
              </PrefillHeader>
              <StyledParagraph size="default" style={{ color: '#6B7280', fontSize: '13px' }}>
                {errorMessage ? errorMessage : isLoggedIn
                  ? "Upload your ID or passport data page, or use your saved profile and we will pre-fill your data for you. Recommended format: JPEG, PNG"
                  : "Upload your ID or passport data page and we will pre-fill your data for you. Recommended format: JPEG, PNG"}
              </StyledParagraph>
              <PrefillActions>
                {isLoggedIn && (
                  <Dropdown menu={profileMenu} trigger={['click']} placement="bottomLeft">
                    <StyledSavedProfileButton shape="round">
                      Use saved profile <DownOutlined style={{ fontSize: '12px' }} />
                    </StyledSavedProfileButton>
                  </Dropdown>
                )}
                <Upload showUploadList={false} beforeUpload={handleOCR} accept="image/*" disabled={isUploadingOcr}>
                  <StyledUploadButton shape="round" loading={isUploadingOcr}>
                    {errorMessage ? "Upload again" : isUploadingOcr ? "Uploading..." : "Upload"}
                  </StyledUploadButton>
                </Upload>
              </PrefillActions>
            </PrefillSectionWrapper>
          )}
        </div>
      </Col>

      <Col span={24}>
        <InfoWrapper>
          <StyledParagraph size="default">
            To avoid boarding complications, enter your details exactly as they appear on your official ID
          </StyledParagraph>
        </InfoWrapper>
      </Col>
      <Col span={12}>
        <Form.Item
          /* eslint-disable-next-line react/jsx-props-no-spreading */
          {...field}
          name={[field.name, 'namePrefix']}
          fieldKey={[field.key, 'namePrefix']}
          rules={[{ required: true, message: t(translations).validations.title }]}
        >
          <StyledSelect options={titleOptions} placeholder={t(translations).input.title} />
        </Form.Item>
      </Col>

      <Col span={12}>
        <Form.Item
          {...field}
          name={[field.name, 'gender']}
          fieldKey={[field.key, 'gender']}
          rules={[{ required: true, message: 'Please select gender' }]}
        >
          <StyledSelect options={genderOptions} placeholder="Gender" />
        </Form.Item>
      </Col>

      <Col span={24}>
        <Form.Item
          /* eslint-disable-next-line react/jsx-props-no-spreading */
          {...field}
          name={[field.name, 'firstName']}
          fieldKey={[field.key, 'firstName']}
          rules={[{ validator: validateNameRequired }]}
        >
          <StyledInput placeholder={t(translations).input.firstName} />
        </Form.Item>
      </Col>

      <Col span={24}>
        <Form.Item
          /* eslint-disable-next-line react/jsx-props-no-spreading */
          {...field}
          name={[field.name, 'lastName']}
          fieldKey={[field.key, 'lastName']}
          rules={[{ validator: validateNameRequired }]}
        >
          <StyledInput placeholder={t(translations).input.lastName} />
        </Form.Item>
      </Col>

      <Col span={24}>
        <Form.Item
          /* eslint-disable-next-line react/jsx-props-no-spreading */
          {...field}
          name={[field.name, 'documentId']}
          fieldKey={[field.key, 'documentId']}
          rules={[{ validator: validateRequired }]}
        >
          <StyledInput placeholder={t(translations).input.documentId} />
        </Form.Item>
      </Col>

      {/* TODO: Hiding the next of kin section for now, remove false && to make it show */}
      {false &&
         (field.key === 0 && isAdult) && (
          <>
            <Col span={24}>
              <Form.Item
                /* eslint-disable-next-line react/jsx-props-no-spreading */
                {...field}
                name={[field.name, 'nextOfKinName']}
                fieldKey={[field.key, 'nextOfKinName']}
                rules={[{ validator: validateRequired, message: t(translations).validations.nok }]}
              >
                <StyledInput placeholder={t(translations).input.nok} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                /* eslint-disable-next-line react/jsx-props-no-spreading */
                {...field}
                name={[field.name, 'nextOfKinEmail']}
                fieldKey={[field.key, 'nextOfKinEmail']}
                rules={[{ validator: validateEmailRequired, message: t(translations).validations.emailInvalid }]}
              >
                <StyledInput placeholder={t(translations).input.nokEmail} />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                /* eslint-disable-next-line react/jsx-props-no-spreading */
                {...field}
                name={[field.name, 'nextOfKinPhoneNumber']}
                fieldKey={[field.key, 'nextOfKinPhoneNumber']}
                rules={[{ validator: validatePhoneRequired, message: t(translations).validations.phone }]}
              >
                <PhoneNumberInput
                  onChange={() => { }}
                  name={"nextOfKinPhoneNumber"}
                  placeholder={t(translations).input.nokPhone}
                  phoneOnly
                />
              </Form.Item>
            </Col>
          </>
        )
      }

      {
        (field.key === 0 && isAdult) && (
          <>
            <Col span={24}>
              <Form.Item
                /* eslint-disable-next-line react/jsx-props-no-spreading */
                {...field}
                valuePropName="checked"
                name={[field.name, 'savePassengerData']}
                fieldKey={[field.key, 'savePassengerData']}
                style={{ marginBottom: 0 }}
              >
                <Checkbox>
                  <CheckBoxLabel>
                    Save my data for future purposes
                  </CheckBoxLabel>
                </Checkbox>
              </Form.Item>
            </Col>

            {
              numberOfAdults > 1 && (
                <Col span={24}>
                  <Form.Item
                    /* eslint-disable-next-line react/jsx-props-no-spreading */
                    {...field}
                    valuePropName="checked"
                    name={[field.name, 'applyToOtherPassengers']}
                    fieldKey={[field.key, 'applyToOtherPassengers']}
                  >
                    <Checkbox>
                      <CheckBoxLabel>
                        Apply details to other passengers
                      </CheckBoxLabel>
                    </Checkbox>
                  </Form.Item>
                </Col>
              )
            }
          </>
        )
      }

    </Row>
  );
}

export default RoadPassengerForm;
